import * as React from "react";
import GoalsContainer from "source/app/presentation/MainPanel/Goals/GoalsContainer";
import { State } from "source/app/services/UI/State/State";
import IconWithText from "../../../../services/Shared/IconWithText/IconWithText";
import { ESharedComponent } from "../../../../services/UI/State/ESharedComponent";
import { IFloatingTool } from "../../../Shared/FloatingTools/FloatingTools";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import { IGoalsContainerState } from "../IGoalsState";
import { IGoalCardsState } from "../GoalCards/Helpers/IGoalCardsState";
import { ShareState } from "../../../../services/UI/State/ShareState";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { Style, StyleClass } from "../../../Shared/Styles/CSS";
import { IGoal } from "motw_core/Entity/IGoal";
import UI from "../../../../services/UI/UI";
import { SetState } from "source/app/services/UI/State/SetState";
import { ReadState } from "../../../../services/UI/State/ReadState";
import shuffle from "lodash-es/shuffle";

export interface IGoalsFloatingToolsInputs {
    goals: GoalsContainer;
}

export const GoalsFloatingTools = {
    GetAll: () => goalsFloatingTools,
    HandleGoalsDirectionFloatingToolClick,
};

const goalsFloatingTools: Readonly<IFloatingTool<IGoalsFloatingToolsInputs>>[] =
    [
        {
            name: `goalsDirectionAll`,
            toRender: (inputs) => (
                <GoalDirectionFloatingTool
                    iconSuffix={EGlyphicon["resize-horizontal"]}
                    direction={EGoalDirection.All}
                />
            ),
            iconColor: `#4bc5ed`,
            tooltip: `Show all quests`,
        },
        {
            name: `goalsDirectionUp`,
            toRender: (inputs) => (
                <GoalDirectionFloatingTool
                    iconSuffix={EGlyphicon["hand-up"]}
                    direction={EGoalDirection.Up}
                />
            ),
            iconColor: `orange`,
            tooltip: `Show upward quests`,
        },
        {
            name: `goalsDirectionDown`,
            toRender: (inputs) => (
                <GoalDirectionFloatingTool
                    iconSuffix={EGlyphicon["hand-down"]}
                    direction={EGoalDirection.Down}
                />
            ),
            iconColor: `greenyellow`,
            tooltip: `Show downward quests`,
        },
        {
            name: `selectRandomGoalCard`,
            toRender: () => <SelectRandomGoalCardFloatingTool />,
            iconColor: `greenyellow`,
            tooltip: `Arrange Quests`,
        },
    ];

export enum EGoalDirection {
    All,
    Up,
    Down,
}

function GoalDirectionFloatingTool({
    iconSuffix,
    direction,
}: {
    iconSuffix: EGlyphicon;
    direction: EGoalDirection;
}) {
    return (
        <IconWithText
            iconSuffix={iconSuffix}
            whenClicked={() => HandleGoalsDirectionFloatingToolClick(direction)}
            displayAsBlock={false}
        />
    );
}

function ShowOrHideAllGoals(direction: EGoalDirection) {
    const showAll =
        direction === EGoalDirection.Up || direction === EGoalDirection.Down;

    const goalsContainer = State.ReadClass<IGoalsContainerState>(
        ESharedComponent.GoalsContainer
    );

    if (goalsContainer) {
        goalsContainer.setState({
            showHiddenGoals: showAll,
        });
    }
}

function HandleGoalsDirectionFloatingToolClick(direction: EGoalDirection) {
    const goals = State.ReadClass<IGoalsContainerState>(
        ESharedComponent.GoalsContainer
    );

    if (goals) {
        goals.setState({ selectedDirection: EGoalDirection[direction] });
        ShowOrHideAllGoals(direction);
    }
}

function SelectRandomGoalCardFloatingTool() {
    const [randomTime, setRandomTime] = React.useState<string>(
        new Date().toISOString()
    );

    ShareState(
        ESharedFunction.SelectRandomGoalCardFloatingTool_randomTime,
        randomTime,
        setRandomTime
    );

    const visibleGoalCardIds = GetVisibleGoalCardIds();
    const reduceIconOpacity = visibleGoalCardIds.length === 0;

    return (
        <IconWithText
            iconSuffix={EGlyphicon.random}
            whenClicked={() =>
                HighlightRandomGoalCard(visibleGoalCardIds)
            }
            displayAsBlock={false}
            withClass={StyleClass(
                `SelectRandomGoalCardFloatingTool_${reduceIconOpacity}`,
                {
                    opacity: reduceIconOpacity ? 0.1 : 1,
                }
            )}
        />
    );
}

export function GetVisibleGoalCardIds() {
    const GoalCards = State.ReadClass<IGoalCardsState>(
        ESharedComponent.GoalCards
    );

    const visibleGoalIds = GoalCards
        ? GoalCards.additionalData.processedGoals.map((goal: IGoal) => goal.id)
        : [];
    return visibleGoalIds;
}

export function HighlightRandomGoalCard(visibleGoalids: number[]) {
    const randomGoalCardId = shuffle(visibleGoalids)[0];

    const goalCards = State.ReadClass<IGoalCardsState>(
        ESharedComponent.GoalCards
    );

    if (goalCards) {
        goalCards.setState({
            randomGoalCardId,
        });

        UI.ScrollToDiv(`#scrollTarget_showGoalCard_${randomGoalCardId}`);

        SetState(
            ESharedFunction.InCenterOfScreen_toRender,
            <FlashGoalCardName goalCardId={randomGoalCardId} />
        );
    }
}

function FlashGoalCardName({ goalCardId }: { goalCardId: number }) {
    React.useEffect(() => {
        const timeoutIntervalId = window.setTimeout(
            () => SetState(ESharedFunction.InCenterOfScreen_toRender, <span />),
            1000
        );

        return () => {
            window.clearTimeout(timeoutIntervalId);
        };
    }, [goalCardId]);

    const goals = State.ReadClass<IGoalsContainerState>(
        ESharedComponent.GoalsContainer
    );

    if (!goals) {
        return <span />;
    }

    const goalName = goals.state.Goals.find(
        (goal) => goal.id === goalCardId
    )!.name;

    return (
        <span
            {...Style(`randomGoalCardHighlightedName`, {
                background: `black`,
                fontSize: `36px`,
                color: `white`,
                width: `100%`,
            })}
        >
            {goalName}
        </span>
    );
}
